import type { TextModePatientInfo } from '@/features/text-mode/types/text-mode.types'
import type {
  GuidedAgeUnit,
  GuidedAssessmentPath,
  GuidedMaternalStatus,
  GuidedModeHandoff,
  GuidedPatientSex,
} from '../types/guided-mode.types'
import {
  NEWBORN_MAX_DAYS,
  selectAssessmentPath,
  shouldCollectMaternalStatus,
} from './assessment-selection'

const { DAYS_PER_YEAR } = require('@shared/clinical-logic/convert/utilities/convertConstants')

const GUIDED_ASSESSMENT_PATHS: GuidedAssessmentPath[] = [
  'newborn', 'child', 'youth', 'antenatal', 'postnatal', 'adult',
]

interface GuidedAge {
  age?: number
  ageUnit: GuidedAgeUnit
}

function asGuidedAssessmentPath(value: unknown): GuidedAssessmentPath | null {
  return typeof value === 'string' && GUIDED_ASSESSMENT_PATHS.includes(value as GuidedAssessmentPath)
    ? value as GuidedAssessmentPath
    : null
}

function guidedAgeFromDays(ageInDays?: number | null): GuidedAge {
  if (typeof ageInDays !== 'number' || !Number.isFinite(ageInDays) || ageInDays <= 0) {
    return { ageUnit: 'years' }
  }
  if (ageInDays <= NEWBORN_MAX_DAYS) return { age: Math.floor(ageInDays), ageUnit: 'days' }
  if (ageInDays < 5 * DAYS_PER_YEAR) {
    return { age: Math.floor((ageInDays * 12) / DAYS_PER_YEAR), ageUnit: 'months' }
  }
  return { age: Math.floor(ageInDays / DAYS_PER_YEAR), ageUnit: 'years' }
}

function guidedMaternalStatus(
  path: GuidedAssessmentPath | null,
  value?: string | null,
): GuidedMaternalStatus {
  if (value === 'antenatal' || value === 'postnatal' || value === 'none') return value
  if (path === 'antenatal' || path === 'postnatal') return path
  return ''
}

function requestedPathAfterDemographics(
  path: GuidedAssessmentPath | null,
  age: GuidedAge,
  sex: GuidedPatientSex,
  maternalStatus: GuidedMaternalStatus,
): GuidedAssessmentPath | null {
  const maternalStatusRequired = shouldCollectMaternalStatus(age.age, age.ageUnit, sex)
  if (path === 'adult' && maternalStatusRequired && !maternalStatus) return null
  return path
}

function reproducibleRequestedPath(
  path: GuidedAssessmentPath | null,
  age: GuidedAge,
  sex: GuidedPatientSex,
  maternalStatus: GuidedMaternalStatus,
): GuidedAssessmentPath | null {
  if (!path) return null
  const selectedPath = selectAssessmentPath({
    age: age.age,
    ageUnit: age.ageUnit,
    sex,
    maternalStatus,
  })
  return selectedPath === path ? path : null
}

export function buildGuidedHandoff(
  requestedPath: unknown,
  patient: TextModePatientInfo | null | undefined,
): GuidedModeHandoff {
  const sourcePath = asGuidedAssessmentPath(requestedPath)
  const sex: GuidedPatientSex = patient?.sex === 'male' || patient?.sex === 'female' ? patient.sex : ''
  const normalizedAge = guidedAgeFromDays(patient?.ageInDays)
  const maternalStatus = guidedMaternalStatus(sourcePath, patient?.maternal_status)
  const candidatePath = requestedPathAfterDemographics(sourcePath, normalizedAge, sex, maternalStatus)
  const path = reproducibleRequestedPath(candidatePath, normalizedAge, sex, maternalStatus)

  return {
    path,
    ...normalizedAge,
    sex,
    maternalStatus,
    identifierValue: patient?.id?.trim() || '',
  }
}
